import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { ArrowRight, Plus, X } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const SCHEDULE_VII = [
  "(i) Eradicating hunger, poverty",
  "(ii) Promoting education, skill development",
  "(iii) Gender equality, empowering women",
  "(iv) Environmental sustainability",
  "(v) Protection of national heritage",
  "(vi) Armed forces welfare",
  "(vii) Sports",
  "(viii) PM National Relief Fund",
  "(ix) Technology incubators",
  "(x) Rural development",
];

const DEMOGRAPHICS = ["women", "youth_18_25", "youth_18_35", "rural", "differently_abled", "scheduled_caste", "scheduled_tribe"];

export default function NewProgram() {
  const nav = useNavigate();
  const [skills, setSkills] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetRole, setTargetRole] = useState("skilled_individual");
  const [targetSkills, setTargetSkills] = useState([]);
  const [targetLocs, setTargetLocs] = useState("");
  const [demos, setDemos] = useState([]);
  const [slots, setSlots] = useState(100);
  const [budget, setBudget] = useState(500000);
  const [perSlot, setPerSlot] = useState(5000);
  const [badge, setBadge] = useState("");
  const [theme, setTheme] = useState(SCHEDULE_VII[1]);
  const [deliv, setDeliv] = useState("ID verification, Skill assessment, Co-branded badge");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/skills").then(({ data }) => setSkills(data.skills)); }, []);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/sponsorship-programs", {
        title, description,
        target_role: targetRole,
        target_skills: targetSkills,
        target_locations: targetLocs.split(",").map((s) => s.trim()).filter(Boolean),
        target_demographics: demos,
        slots: Number(slots),
        budget_total: Number(budget),
        per_slot_value: Number(perSlot),
        co_branded_badge: badge,
        schedule_vii_theme: theme,
        deliverables: deliv.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Program live");
      nav(`/sponsorship-programs/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error creating program");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">CSR / Sponsorship</div>
        <h1 className="text-3xl font-bold text-[var(--as-primary)] mt-1" style={{fontFamily:"Inter"}}>Launch a sponsorship program</h1>
        <p className="text-[var(--as-text-secondary)] mt-2">Define a cohort, allocate a budget, and we'll handle verification + impact reporting.</p>

        <form onSubmit={submit} className="as-card p-7 mt-8 space-y-5" data-testid="new-program-form">
          <div>
            <label className="as-label">Program title</label>
            <input className="as-input" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bosch Electrician 1000 — Karnataka" data-testid="prog-title" />
          </div>
          <div>
            <label className="as-label">Description</label>
            <textarea className="as-input min-h-[120px]" required value={description} onChange={(e) => setDescription(e.target.value)} data-testid="prog-desc" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="as-label">Target role</label>
              <select className="as-input" value={targetRole} onChange={(e) => setTargetRole(e.target.value)} data-testid="prog-target-role">
                <option value="skilled_individual">Skilled individual</option>
                <option value="msme">MSME</option>
                <option value="any">Any</option>
              </select>
            </div>
            <div>
              <label className="as-label">Slots</label>
              <input className="as-input" type="number" min={1} value={slots} onChange={(e) => setSlots(e.target.value)} data-testid="prog-slots" />
            </div>
            <div>
              <label className="as-label">Per-slot value (₹)</label>
              <input className="as-input" type="number" min={0} value={perSlot} onChange={(e) => setPerSlot(e.target.value)} data-testid="prog-perslot" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="as-label">Total budget (₹)</label>
              <input className="as-input" type="number" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="prog-budget" />
            </div>
            <div>
              <label className="as-label">CSR Schedule VII theme</label>
              <select className="as-input" value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="prog-theme">
                {SCHEDULE_VII.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="as-label">Target trades / skills</label>
            <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
              {skills.map((s) => (
                <button key={s} type="button" onClick={() => toggle(targetSkills, setTargetSkills, s)}
                  className={`as-badge-skill ${targetSkills.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}>
                  {targetSkills.includes(s) ? <X size={12} weight="bold" /> : <Plus size={12} weight="bold" />} {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="as-label">Target locations (comma-separated)</label>
            <input className="as-input" value={targetLocs} onChange={(e) => setTargetLocs(e.target.value)} placeholder="Bengaluru, Mysore" data-testid="prog-locs" />
          </div>
          <div>
            <label className="as-label">Demographics (optional focus)</label>
            <div className="flex flex-wrap gap-2">
              {DEMOGRAPHICS.map((d) => (
                <button key={d} type="button" onClick={() => toggle(demos, setDemos, d)}
                  className={`as-badge-skill ${demos.includes(d) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}>
                  {d.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="as-label">Co-branded badge name</label>
            <input className="as-input" value={badge} onChange={(e) => setBadge(e.target.value)} placeholder="Bosch Verified Electrician" data-testid="prog-badge" />
          </div>
          <div>
            <label className="as-label">Deliverables (comma-separated)</label>
            <input className="as-input" value={deliv} onChange={(e) => setDeliv(e.target.value)} data-testid="prog-deliv" />
          </div>

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="prog-submit">
              {busy ? "..." : "Launch program"} <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
